import React, { memo } from 'react';

import { Button, Spinner } from '@ergeon/core-components';
import { PauseIcon, PlayPauseIcon, MicrophoneIcon, PhoneXMarkIcon, UserPlusIcon } from '@ergeon/icons/hero/micro';

import { formatSecondsToTimeString } from '../../utils';

import { ActiveCallControlsProps } from './types';

const getHoldButtonIcon = (holdPending: boolean | undefined, isHolded: boolean) => {
  if (holdPending) {
    return (
      <span data-testid="hold-pending-spinner">
        <Spinner active className="size-4 shrink-0" color="gray" size={16} />
      </span>
    );
  }
  if (isHolded) {
    return <PlayPauseIcon className="size-4 shrink-0" data-testid="resume-call-icon" />;
  }
  return <PauseIcon className="size-4 shrink-0" data-testid="pause-call-icon" />;
};

const ActiveCallControls: React.FC<ActiveCallControlsProps> = memo(
  ({ isHolded, isMuted, loadingStates, callDuration, holdCall, toggleMute, hangUp, hangupCooldown, onInviteAgent, inviteAgentDisabled, holdPending }) => (
    <div className="flex items-center gap-1">
      <Button
        data-testid="hold-call-button"
        disabled={loadingStates.hold || holdPending}
        flavor="regular"
        isSquare
        onClick={holdCall}
        size="medium"
        taste="line"
        title={holdPending ? 'Hold engaging...' : 'Hold Call'}>
        {getHoldButtonIcon(holdPending, isHolded)}
      </Button>
      <Button
        aria-label={isMuted ? 'Unmute call' : 'Mute call'}
        data-testid="mute-button"
        disabled={loadingStates.mute}
        flavor={isMuted ? 'danger' : 'regular'}
        isSquare
        onClick={toggleMute}
        size="medium"
        taste="line"
        title={isMuted ? 'Unmute' : 'Mute'}>
        <MicrophoneIcon className="size-4 shrink-0" />
      </Button>
      <Button
        aria-label="Invite agent to call"
        data-testid="invite-agent-button"
        disabled={inviteAgentDisabled}
        flavor="regular"
        isSquare
        onClick={onInviteAgent}
        size="medium"
        taste="line"
        title="Invite Agent">
        <UserPlusIcon className="size-4 shrink-0" />
      </Button>
      <Button
        aria-label="End current call"
        className="min-w-24"
        data-testid="hangup-button"
        disabled={loadingStates.hangup || hangupCooldown}
        flavor="danger"
        onClick={hangUp}
        size="medium"
        title="Hang Up">
        <PhoneXMarkIcon className="size-4 shrink-0" data-testid="hangup-icon" />
        {formatSecondsToTimeString(callDuration)}
      </Button>
    </div>
  ),
);

ActiveCallControls.displayName = 'ActiveCallControls';

export default ActiveCallControls;
